<template>
  <div class="header">
    <div v-if="showLabel" class="header__title">
      <div class="header__title-text">
        <slot name="header-title" />
      </div>
      <HelpTooltip>
        <template #title>
          <slot name="header-title" />
        </template>
        <template #description>
          <ul class="header__help-tooltip-list">
            <li
              v-if="hasHeaderTooltipDescriptionText"
              class="header__help-tooltip-list-item"
            >
              <slot name="header-tooltip-description-text" />
            </li>
            <li class="header__help-tooltip-list-item">
              <slot name="header-tooltip-recommended-size" />
            </li>
          </ul>
        </template>
      </HelpTooltip>
    </div>
    <div class="header__actions">
      <ButtonMenu
        v-if="showEditButton"
        :shadow="headerMenuInline"
        :type="buttonType"
      >
        <template #button-label>{{ $t('image.EDIT_IMAGE') }}</template>
        <template #menu-items>
          <ButtonMenuItem @click="openFileExplorer()">
            <template #icon>
              <UploadIcon />
            </template>
            <template #label>{{ $t('image.UPLOAD_IMAGE') }}</template>
          </ButtonMenuItem>

          <ButtonMenuItem v-if="canUseUnsplash" @click="openUnsplash()">
            <template #icon>
              <PictureIcon />
            </template>
            <template #label>{{ $t('image.SELECT_IMAGE') }}</template>
          </ButtonMenuItem>

          <ButtonMenuItem
            v-if="canRepositionImage"
            separate-top
            @click="repositionImage()"
          >
            <template #icon>
              <RepositionIcon />
            </template>
            <template #label>{{ $t('image.REPOSITION_IMAGE') }}</template>
          </ButtonMenuItem>

          <ButtonMenuItem
            v-if="canRestoreOriginalImage"
            separate-top
            @click="restoreOriginalImage()"
          >
            <template #icon>
              <RestoreIcon />
            </template>
            <template #label>{{ $t('image.RESTORE_ORIGINAL_IMAGE') }}</template>
          </ButtonMenuItem>

          <ButtonMenuItem
            v-if="canDeleteImage"
            separate-top
            danger
            @click="deleteImage()"
          >
            <template #icon>
              <TrashIcon />
            </template>
            <template #label>{{ $t('image.DELETE_IMAGE') }}</template>
          </ButtonMenuItem>
        </template>
      </ButtonMenu>
    </div>
  </div>
</template>

<script>
import ButtonMenu from '@/components/generics/ButtonMenu'
import ButtonMenuItem from '@/components/generics/ButtonMenuItem'
import HelpTooltip from '@/components/generics/HelpTooltip.vue'
import PictureIcon from '@/components/icon_components/PictureIcon.vue'
import RepositionIcon from '@/components/icon_components/RepositionIcon.vue'
import RestoreIcon from '@/components/icon_components/RestoreIcon.vue'
import TrashIcon from '@/components/icon_components/TrashIcon.vue'
import UploadIcon from '@/components/icon_components/UploadIcon.vue'

export default {
  components: {
    ButtonMenu,
    ButtonMenuItem,
    HelpTooltip,
    PictureIcon,
    RepositionIcon,
    RestoreIcon,
    TrashIcon,
    UploadIcon,
  },
  props: {
    showEditButton: {
      type: Boolean,
      required: false,
      default: false,
    },
    canRepositionImage: {
      type: Boolean,
      required: false,
      default: false,
    },
    canRestoreOriginalImage: {
      type: Boolean,
      required: false,
      default: false,
    },
    canUseUnsplash: {
      type: Boolean,
      required: false,
      default: false,
    },
    canDeleteImage: {
      type: Boolean,
      required: false,
      default: false,
    },
    headerMenuInline: {
      type: Boolean,
      required: true,
    },
  },

  emits: [
    'onOpenFileExplorer',
    'onOpenUnsplash',
    'onRepositionImage',
    'onRestoreOriginalImage',
    'onDeleteImage',
  ],
  computed: {
    hasHeaderTooltipDescriptionText() {
      return this.$slots['header-tooltip-description-text']
    },

    buttonType() {
      return this.headerMenuInline ? 'edit' : 'text'
    },

    showLabel() {
      return !this.headerMenuInline
    },
  },

  methods: {
    openFileExplorer() {
      this.$emit('onOpenFileExplorer')
    },
    openUnsplash() {
      this.$emit('onOpenUnsplash')
    },
    repositionImage() {
      this.$emit('onRepositionImage')
    },
    restoreOriginalImage() {
      this.$emit('onRestoreOriginalImage')
    },
    deleteImage() {
      this.$emit('onDeleteImage')
    },
  },
}
</script>

<style lang="scss" scoped>
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: $spacing-m; // To accommodate ButtonMenu height

  &__title {
    display: flex;
    gap: $spacing-4xs;
  }

  &__title-text {
    @include formLabelStyle;
    margin-bottom: 0;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: $spacing-3xs;
  }

  &__help-tooltip-list {
    padding: $spacing-5xs;
    margin-left: $spacing-3xs;
  }

  &__help-tooltip-list-item {
    list-style: initial;
  }
}
</style>
